<script setup lang="ts">
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import {
  useVueTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  FlexRender
} from '@tanstack/vue-table';
import { FwbButton, FwbSpinner } from 'flowbite-vue';
import debounce from 'lodash/debounce';
import IconSvg from '@base-shared/components/IconSvg.vue';

import { TIMEOUT_300_MS } from '@base-shared/utils/constants';
import { useScreenSize } from '@base-shared/composables/use-screen-size';

import type { ColumnDef, Table, SortingState, Header, Row, FilterFnOption } from '@tanstack/vue-table';

interface GenericRow {
  [key: string]: unknown;
}

const props = withDefaults(defineProps<{
  columns: unknown[]; // TODO: figure out a way to set a type for columns
  data: GenericRow[];
  totalDataCount?: number;

  stickyHeader?: boolean;
  title?: string;
  searchPlaceholder?: string;
  emptyPlaceholder?: string;
  emptyFilteredPlaceholder?: string;

  globalFilterTextPreValue?: string | null;
  globalFilterType?: 'manual' | 'client-side'; // manual for server-side and client-side for tanstack api
  isFilteringState?: boolean; // for globalFilterType: 'manual' to keep searching state
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  customGlobalFilterFn?: (row: Row<any>, columnId: string, filterValue: string) => boolean; // TODO: figure out a way to set a type for columns

  sortType?: 'manual' | 'client-side'; // manual for server-side and client-side for tanstack api

  paginationType?: 'per-page' | 'load-more' | 'infinite-scroll'; // per-page for page style, load-more for single button to load next
  hasNextPage?: boolean;
  hasPreviousPage?: boolean;

  showHeader?: boolean;
  showFooter?: boolean;
  showInitTableLoader?: boolean;
  showPaginationLoader?: boolean;
  showSearch?: boolean;

  rowHeight?: number; // default to 54px
  initialSortingState?: SortingState;

  disableHeightAutoCalculation?: boolean; // to disable automatic calculation of max table height and rows count based on the viewport height
}>(), {
  stickyHeader: true,

  showSearch: true,

  globalFilterType: 'client-side',
  sortType: 'client-side',
  rowHeight: 54
});

const emit = defineEmits<{
  (e: 'loadMore'): void;
  (e: 'headerClick', header: Header<GenericRow, unknown>): void;

  (e: 'globalFilterChange' | 'search', text: string): void;
  (e: 'sortChange', sorting: SortingState): void;

  (e: 'tableRowResize', obj: {
    maxTableHeight: number;
    maxTableRowsCount: number;
  }): void;
}>();

const { screenHeight } = useScreenSize();
const debouncedResize = debounce(computeVerticalMaxRows, TIMEOUT_300_MS);

const containerRef = ref<HTMLElement | null>(null);
const headerContainerRef = ref<HTMLElement | null>(null);
const footerContainerRef = ref<HTMLElement | null>(null);
const dataTableContainerRef = ref<HTMLElement | null>(null);

const columnsContainerRef = ref<HTMLElement | null>(null);
const rowsContainerRef = ref<HTMLElement | null>(null);
const rowItemRefs = ref<HTMLElement[]>([]);
const previousLastRowItemRef = ref<HTMLElement | null>(null);

const tableInstance = ref<Table<GenericRow> | null>(null);
const globalFilterText = ref<string>(props.globalFilterTextPreValue ?? '');
const sortData = ref<SortingState>([]);

const localData = ref<GenericRow[]>([...props.data ?? []]);

const computedMaxTableHeight = ref<number | null>(null);
const computedMaxTableRowsCount = ref<number | null>(null);

const headerGroup = computed(() => tableInstance.value?.getHeaderGroups?.() || []);
const filteredRows = computed(() => tableInstance.value?.getRowModel?.()?.rows || []);

const hasTotalDataCount = computed(() => typeof props.totalDataCount === 'number' && props.totalDataCount > 0);
const showEmptyPlaceholder = computed(() => !localData.value?.length || !filteredRows.value?.length);
const emptyPlaceholderText = computed(() => {
  if (!showEmptyPlaceholder.value) {
    return null;
  }

  if (props.globalFilterType === 'manual') {
    return props.isFilteringState
      ? props.emptyFilteredPlaceholder
      : props.emptyPlaceholder;
  }

  return !localData.value?.length
    ? props.emptyPlaceholder
    : props.emptyFilteredPlaceholder;
});

watch(() => props.data, (newData) => {
  // Mark previous last row item ref
  setLastRowItemRef();
  /**
   * To be able for tanstack to re-rendar the table, we have to break
   * vue's reactivity by setting new instance to the `data
   */
  localData.value = newData?.length
    ? [...newData]
    : [];
}, {
  deep: true
});

watch(screenHeight, debouncedResize);

function setTableGlobalFilter(filter: string) {
  tableInstance.value?.setGlobalFilter?.(String(filter ?? '').trim());
}

function onHeaderCellClick(header: Header<GenericRow, unknown>, event: Event) {
  if (props.sortType === 'client-side') {
    header.column.getToggleSortingHandler()?.(event);
  }

  emit('headerClick', header);
}

function setLastRowItemRef() {
  previousLastRowItemRef.value = filteredRows.value?.length
    ? rowItemRefs.value[filteredRows.value.length - 1] ?? null
    : null;
}

function scrollToBottom(rowItemElement?: HTMLElement | null) {
  if (!dataTableContainerRef.value) {
    return;
  }

  if (rowItemElement) {
    rowItemElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  if (previousLastRowItemRef.value) {
    previousLastRowItemRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return;
  }

  dataTableContainerRef.value.scrollTop = dataTableContainerRef.value.scrollHeight;
}

function computeVerticalMaxRows() {
  if (props.disableHeightAutoCalculation) {
    return;
  }

  const containerRect = containerRef.value?.getBoundingClientRect();

  // Exit immediateley if for some reason containerRef is undefined
  if (!containerRect) {
    console.warn('DataTable: Invalid container rect');
    return;
  }

  // Get available slots heights
  const headerContainerHeight = props.showHeader
    ? headerContainerRef.value?.getBoundingClientRect?.()?.height ?? 0
    : 0;
  const footerContainerHeight = props.showFooter
    ? footerContainerRef.value?.getBoundingClientRect?.()?.height ?? 0
    : 0;

  const dataTableContainerHeight = dataTableContainerRef.value?.getBoundingClientRect?.()?.height ?? 0;
  const tableColumnsHeight = columnsContainerRef.value?.getBoundingClientRect?.()?.height ?? 0;

  // Get parent component element's margins
  const parentElStyle = containerRef.value?.parentElement
    ? getComputedStyle(containerRef.value.parentElement)
    : null;
  const parentElXMarginTop = parseFloat(parentElStyle?.marginTop ?? '');
  const parentElXMarginBot = parseFloat(parentElStyle?.marginBottom ?? '');
  const parentElTotalXmargin = parentElXMarginTop + parentElXMarginBot;

  // Total of max height DataTable component can use
  const maxContainerHeight = screenHeight.value - containerRect.top;
  const currentContainerHeight = containerRect.height;
  const occupiedContainerHeight = headerContainerHeight + dataTableContainerHeight + footerContainerHeight;

  // Stuff like border width that is counted as container's height
  const excessContainerHeight = currentContainerHeight - occupiedContainerHeight + parentElTotalXmargin;

  // Actual maximum height inside of the containerRef
  const maxTableHeight = maxContainerHeight - excessContainerHeight - headerContainerHeight - footerContainerHeight;
  // Actual maximum table rows height
  const maxTableRowsHeight = maxTableHeight - tableColumnsHeight;

  // Count maximum table rows count using props.rowHeight against maxTableRowsHeight
  const maxTableRowsCount = Math.floor(maxTableRowsHeight / props.rowHeight);
  const maxRowCount = maxTableRowsCount < 3 ? 3 : maxTableRowsCount;

  computedMaxTableHeight.value = maxTableHeight;
  computedMaxTableRowsCount.value = maxTableRowsCount;
  emit('tableRowResize', { maxTableHeight, maxTableRowsCount: maxRowCount });
}

onMounted(() => {
  tableInstance.value = useVueTable<GenericRow>({
    manualPagination: true,
    manualSorting: props.sortType === 'manual',
    manualFiltering: props.globalFilterType === 'manual',

    columns: props.columns as ColumnDef<GenericRow>[],
    get data() {
      return localData.value;
    },
    state: {
      get globalFilter() {
        return globalFilterText.value;
      },
      get sorting() {
        return sortData.value;
      }
    },

    globalFilterFn: (props.customGlobalFilterFn || 'includesString') as FilterFnOption<GenericRow>,

    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onGlobalFilterChange: (updaterOrValue) => {
      const newValue = typeof updaterOrValue === 'function'
        ? updaterOrValue(globalFilterText.value)
        : updaterOrValue;

      globalFilterText.value = (newValue ?? '').trim();
      emit('globalFilterChange', globalFilterText.value);
    },
    onSortingChange: (updaterOrValue) => {
      sortData.value = typeof updaterOrValue === 'function'
        ? updaterOrValue(sortData.value)
        : updaterOrValue;

      emit('sortChange', sortData.value);
    }
  });

  nextTick(() => {
    // Perform initial sorting state if available
    if (tableInstance.value && props.initialSortingState) {
      tableInstance.value.setSorting(props.initialSortingState);
    }

    // Perform initial max rows computation
    computeVerticalMaxRows();
  });
});

defineExpose({
  scrollToBottom
});
</script>

<template>
  <div
    ref="containerRef"
    class="data-table-wrapper"
  >
    <div
      v-if="showHeader"
      ref="headerContainerRef"
      class="flex flex-col items-start p-4 gap-4"
      data-component-element-id="data-table-header"
    >
      <h1
        class="text-base text-gray-900 font-semibold"
        data-component-element-id="data-table-header-title"
      >
        {{ title }}
      </h1>
      <div
        v-if="showSearch"
        class="flex items-center justify-between w-full gap-2"
      >
        <FormInput
          :model-value="globalFilterText"
          :placeholder="searchPlaceholder"
          data-component-element-id="data-table-header-global-filter-input"
          class="w-96"
          size="sm"
          @input="(e: Event) => setTableGlobalFilter((e.target as HTMLInputElement).value)"
          @change="$emit('search', globalFilterText)"
        >
          <template #prefix>
            <IconSvg
              class="h-5! w-5! text-gray-500"
              name="flowbite:search-outline"
            />
          </template>

          <template #suffix>
            <div
              v-if="globalFilterText?.length > 0"
              class="absolute top-0 right-3 h-[38px] flex items-center cursor-pointer"
              @click="globalFilterText = ''; $emit('search', globalFilterText)"
            >
              <IconSvg
                name="flowbite:close-outline"
                data-component-element-id="data-table-header-global-filter-input-clear"
                class="w-4! h-4!"
              />
            </div>
          </template>
        </FormInput>

        <slot name="header-actions" />
      </div>
      <slot name="header-extras" />
    </div>

    <div
      ref="dataTableContainerRef"
      class="data-table-container"
      :style="[
        typeof computedMaxTableHeight === 'number'
          ? { maxHeight: computedMaxTableHeight + 'px' }
          : {},
      ]"
    >
      <table class="data-table">
        <thead
          ref="columnsContainerRef"
          :class="{ 'sticky-header': !!stickyHeader }"
        >
          <tr
            v-for="group in headerGroup"
            :key="group.id"
            :data-component-element-id="`data-table-header-row-${group.id}`"
          >
            <th
              v-for="header in group.headers"
              :key="header.id"
              :data-component-element-id="`data-table-header-cell-${header.id}`"
              :class="{
                'cursor-pointer select-none': header.column.getCanSort(),
                'sticky-header': !!stickyHeader,
              }"
              :style="{
                width: (header?.column?.columnDef?.meta as any)?.width || undefined,
                minWidth: (header?.column?.columnDef?.meta as any)?.minWidth || undefined,
              }"
              class="h-[50px] text-xs text-gray-500 font-semibold uppercase bg-gray-50 border-b border-gray-200 sticky top-0 p-4 content-center"
              @click="onHeaderCellClick(header, $event)"
            >
              <div class="flex items-center gap-2">
                <FlexRender
                  v-if="!header.isPlaceholder"
                  :render="header.column.columnDef.header"
                  :props="header.getContext()"
                />

                <template v-if="sortType && header.column.getCanSort()">
                  <IconSvg
                    v-if="!header.column.getIsSorted()"
                    :data-component-element-id="`data-table-header-cell-sort-${header.id}`"
                    class="w-4! h-4!"
                    name="flowbite:chevron-sort-outline"
                  />
                  <IconSvg
                    v-else-if="header.column.getIsSorted() === 'asc'"
                    :data-component-element-id="`data-table-header-cell-sort-${header.id}`"
                    class="w-4! h-4!"
                    name="flowbite:angle-up-outline"
                  />
                  <IconSvg
                    v-else-if="header.column.getIsSorted() === 'desc'"
                    :data-component-element-id="`data-table-header-cell-sort-${header.id}`"
                    class="w-4! h-4!"
                    name="flowbite:angle-down-outline"
                  />
                </template>
              </div>
            </th>
          </tr>
        </thead>
        <tbody ref="rowsContainerRef">
          <!-- Loader -->
          <template v-if="showInitTableLoader && headerGroup?.[0]?.headers?.length">
            <tr
              v-for="item in 5"
              :key="`placeholder-item-${item}`"
              data-component-element-id="data-table-body-row-loader"
              class="bg-white border-b border-gray-200"
            >
              <td
                v-for="cell in headerGroup[0].headers"
                :key="`placeholder-col-${cell.id}`"
                class="px-4"
                :class="`h-[${rowHeight}px]`"
              >
                <div class="animate-pulse">
                  <span class="inline-block min-h-6 w-full flex-auto cursor-wait bg-gray-400 align-middle text-base text-neutral-700 opacity-50 rounded-md" />
                </div>
              </td>
            </tr>
          </template>
          <!-- Empty Table Items -->
          <template v-else-if="showEmptyPlaceholder">
            <tr
              class="data-table-body-row-empty-placeholder bg-white border-b border-gray-200 h-48"
              data-component-element-id="data-table-body-row-empty-placeholder"
            >
              <td
                v-if="headerGroup?.[0]?.headers?.length"
                :colspan="headerGroup[0].headers.length"
                class="py-6 px-4 text-gray-500 text-sm font-normal h-5 text-center"
              >
                {{ emptyPlaceholderText }}
              </td>
            </tr>
          </template>
          <template v-else>
            <template
              v-for="(row, index) in filteredRows"
              :key="row.id"
            >
              <slot
                name="body-row"
                :row="row"
                :index="index"
              >
                <tr
                  :ref="(el: Element | ComponentPublicInstance | null) => { rowItemRefs[index] = (el as HTMLElement) }"
                  :data-component-element-id="`data-table-body-row-${index}`"
                  class="bg-white border-b border-gray-200"
                >
                  <td
                    v-for="cell in row.getVisibleCells()"
                    :key="cell.id"
                    :data-component-element-id="`data-table-body-row-${index}-cell-${cell.id}`"
                    class="h-[54px] px-4 text-xs"
                  >
                    <FlexRender
                      :render="cell.column.columnDef.cell"
                      :props="cell.getContext()"
                    />
                  </td>
                </tr>
              </slot>
            </template>
          </template>
        </tbody>
      </table>
    </div>

    <div
      v-if="showFooter"
      ref="footerContainerRef"
      data-component-element-id="data-table-footer"
      class="flex items-center justify-between p-4 gap-4 border-t border-gray-200 min-h-[67px]"
    >
      <template v-if="paginationType === 'load-more' && localData.length > 0">
        <FwbButton
          class="justify-center py-2"
          color="light"
          size="xs"
          data-component-element-id="data-table-load-more-btn"
          :disabled="!hasNextPage || showPaginationLoader"
          @click="$emit('loadMore')"
        >
          <template #prefix>
            <FwbSpinner
              v-if="showPaginationLoader"
              color="gray"
            />
            <IconSvg
              v-else
              name="flowbite:arrow-down-outline"
              class="w-4! h-4!"
            />
          </template>

          {{ showPaginationLoader ? $t('texts.loadingWDots') : $t('buttons.loadMore') }}
        </FwbButton>
      </template>

      <span
        v-show="!showInitTableLoader && (localData.length > 0 || isFilteringState || hasTotalDataCount)"
        data-component-element-id="data-table-footer-count"
        class="ml-auto text-sm text-gray-500"
      >
        <span
          v-if="localData.length <= 0 && !isFilteringState"
          class="text-back"
        >
          {{ totalDataCount }}
        </span>
        <i18n-t
          v-else-if="!hasTotalDataCount"
          keypath="texts.tablePageCount"
          tag="span"
        >
          <template #min>
            <span class="text-gray-900">{{ filteredRows.length >= 1 ? '1' : '0' }}</span>
          </template>
          <template #max>
            <span class="text-gray-900">{{ filteredRows.length }}</span>
          </template>
        </i18n-t>
        <i18n-t
          v-else
          keypath="texts.tablePageCountWTotal"
          tag="span"
        >
          <template #min>
            <span class="text-gray-900">{{ filteredRows.length >= 1 ? '1' : '0' }}</span>
          </template>
          <template #max>
            <span class="text-gray-900">{{ filteredRows.length }}</span>
          </template>
          <template #total>
            <span class="text-gray-900">{{ totalDataCount }}</span>
          </template>
        </i18n-t>
      </span>
    </div>
  </div>
</template>

<style lang="css" scoped>
@reference '@base-shared/assets/css/tailwind.css';

.data-table-wrapper {
  @apply relative w-full bg-white border border-gray-200 rounded-lg shadow;

  .data-table-container {
    @apply max-h-[590px] overflow-auto scroll-smooth;
  }

  .data-table {
    @apply table-auto relative w-full text-sm text-left rtl:text-right text-gray-500;

    thead {
      &.sticky-header {
        @apply sticky top-0 z-10;
      }

      th:after,
      th:before {
        content: '';
        @apply absolute left-0 w-full z-10;
      }

      th:before {
        @apply -top-px border-t border-gray-200 z-10;
      }

      th:after {
        @apply -bottom-px border-b border-gray-200 z-10;
      }
    }

    tbody {
      tr:last-child {
        @apply border-0;
      }
    }
  }

  ::-webkit-scrollbar {
    @apply w-5 h-5;
  }

  ::-webkit-scrollbar-track {
    border-top: 1px solid transparent;
    border-left: 1px solid transparent;
    @apply bg-gray-50 border-gray-200 border-l-[1px];
  }

  ::-webkit-scrollbar-thumb {
    @apply bg-gray-300 rounded-full border-[6px] border-solid bg-clip-content border-transparent;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply bg-gray-400;
  }
}
</style>
